import { ProductDetailsResponse, ProductRole } from "@/@types/Products.ts";
import People from "@/assets/icons/People.tsx";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete.tsx";
import Divider from "@/components/shared/Divider/Divider";
import CustomSheet from "@/components/shared/Sheet/Sheet.tsx";
import CTATable from "@/components/shared/TableListCustom/CTATable.tsx";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom.tsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";
import { CoProducer } from "@/services/coproducer.service.ts";
import useModalStore from "@/store/useModalStore";
import { Edit, Mail, Unlink } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useParams } from "react-router-dom";
import { useCoproducers } from "./hooks/useCoProducer.ts";
import SideQuickEdit from "./Partials/SideQuickEdit";
import { usePermissions } from "@/auth/PermissionsContext";

export default function CoProducerList() {
  const { t } = useTranslation("coproducer");
  const { openModal, modals, closeModal } = useModalStore();
  const [selectedCoProducer, setSelectedCoProducer] =
    useState<CoProducer | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { id } = useParams<{ id: string }>();
  const { product } = useOutletContext<{ product: ProductDetailsResponse }>();

  const { hasPermission } = usePermissions();
  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;
  const canInvite = isProducer && hasPermission('coproducers.invite_coproducer');
  const canEdit = isProducer && hasPermission('coproducers.edit_coproducer');
  const canRemove = isProducer && hasPermission('coproducers.remove_coproducer');

  const {
    currentPage,
    setCurrentPage,
    isLoading,
    deleteCoproducer,
    data: coproducers,
  } = useCoproducers();

  const handleEdit = (producer: CoProducer) => {
    setSelectedCoProducer(producer);
    setIsEditing(true);
    openModal("edit-co-producer");
  };

  const headers = [
    { title: t("header.user"), key: "user" },
    { title: t("header.commission"), key: "value" },
    { title: "", key: "actions" },
  ];

  return (
    <Card className="w-full mt-8 overflow-x-auto">
      <div className="flex md:items-center flex-col md:flex-row w-full p-4 justify-between max-md:gap-3">
        <h1 className="text-base font-semibold">{t("coproducer")}</h1>
        {canInvite && (
          <Button
            className="w-full md:max-w-[202px]"
            onClick={() => openModal("invite-co-producer")}
            data-cy="btn-open-create-coproducer"
          >
            <Mail />
            {t("createCoProducer")}
          </Button>
        )}
      </div>

      <Divider />

      <TableListCustom
        emptyDescriptionMessage={t("empty_coproducers.description")}
        emptyIconMessage={<People />}
        emptyTitleMessage={t("empty_coproducers.title")}
        headers={headers}
        isLoadingData={isLoading}
        data={coproducers}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      >
        {coproducers?.data?.map((producer) => (
          <TableRow
            key={producer.id}
            className="h-[64px] border-b border-b-colorBorder hover:bg-transparent"
            data-cy="table-row"
          >
            <TableCell className="font-medium">
              <div className="flex flex-col gap-2">
                <span
                  className={`text-sm font-medium ${
                    producer?.invite?.user?.name
                      ? "text-white"
                      : "text-[#A1A1AA]"
                  }`}
                >
                  {producer?.invite?.user?.name || t("waitingRegistration")}
                </span>
                <span className="text-xs text-[#A1A1AA]">{producer.email}</span>
              </div>
            </TableCell>
            <TableCell>{producer.invite.commission_value_formatted ?? producer.invite.percentage_formatted ?? "—"}</TableCell>
            <TableCell className="w-[50px]">
              {(canEdit || canRemove) && (
                <CTATable
                  items={[
                    ...(canEdit ? [{
                      id: 1,
                      label: t("edit"),
                      icon: <Edit />,
                      action: () => { handleEdit(producer); },
                    }] : []),
                    ...(canRemove ? [{
                      id: 2,
                      label: t("delete"),
                      icon: <Unlink color="#F87171" />,
                      action: () => {
                        setSelectedCoProducer(producer);
                        openModal("delete-coproducer");
                      },
                    }] : []),
                  ]}
                />
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableListCustom>

      {canRemove && (
        <DialogDelete
          isOpen={modals["delete-coproducer"]}
          onClose={() => closeModal("delete-coproducer")}
          onDelete={() => deleteCoproducer(selectedCoProducer.id)}
          title={t("delete_coproducer_title")}
          description={t("delete_coproducer_description")}
        />
      )}

      {canEdit && selectedCoProducer && isEditing && (
        <CustomSheet
          open={modals["edit-co-producer"]}
          onClose={() => {
            setIsEditing(false);
            closeModal("edit-co-producer");
          }}
        >
          <SideQuickEdit
            coProducer={selectedCoProducer}
            isOpen={isEditing}
            onClose={() => {
              setIsEditing(false);
              closeModal("edit-co-producer");
            }}
          />
        </CustomSheet>
      )}
    </Card>
  );
}
